import { type PropsWithChildren, Suspense, useEffect, useRef } from 'react';
import { enableViews, flagListDisplayPreferences } from '@gonfalon/dogfood-flags/dogfood-flags';
import { QuietErrorBoundary } from '@gonfalon/error-boundaries/QuietErrorBoundary';
import { useSlots } from '@gonfalon/react/useSlots';
import clsx from 'clsx';
import invariant from 'tiny-invariant';

import { Breadcrumbs, type BreadcrumbTrailItem } from '../../Breadcrumbs';
import { ChangeHistoryTrigger } from '../../ChangeHistoryTrigger';
import { EnvironmentController } from '../../EnvironmentController';
import { LayoutHeader, LayoutHeaderActions, LayoutHeaderContext } from '../../LayoutHeader';
import { ShortcutsTrigger } from '../../ShortcutsTrigger';
import { Action } from '../Action';
import { DisplayPreferences } from '../DisplayPreferences';
import { Filters } from '../Filters';
import { Ordering } from '../Ordering';
import { ResourceSettings } from '../ResourceSettings';
import { useInlineChangeHistory } from '../useInlineChangeHistory';
import { useMaxWidthAndCenteredLayoutContent } from '../useMaxWidthAndCenteredLayoutContent';
import { Views } from '../Views';
import { ViewsFilter } from '../ViewsFilter';

import styles from './ProjectCollectionLayout.module.css';

export function ProjectCollectionLayout({
  children,
  breadcrumbTrail,
}: PropsWithChildren<{ breadcrumbTrail?: BreadcrumbTrailItem[] }>) {
  const [{ context, content, banner, footer }] = useSlots(children, {
    content: ProjectCollectionContent,
    footer: ProjectCollectionFooter,
    banner: ProjectCollectionBanner,
    context: ProjectCollectionContext,
  });

  invariant(content, 'ProjectCollectionLayout must include a ProjectCollectionContent');

  const isMaxWidthAndCenteredLayoutContent = useMaxWidthAndCenteredLayoutContent();
  const hasInlineChangeHistory = useInlineChangeHistory();

  return (
    <div className={styles.collection}>
      <LayoutHeader isMaxWidthAndCentered={isMaxWidthAndCenteredLayoutContent}>
        {breadcrumbTrail ? (
          <LayoutHeaderContext>
            <Breadcrumbs trail={breadcrumbTrail} />
          </LayoutHeaderContext>
        ) : null}
        <LayoutHeaderActions>
          {enableViews() ? <ViewsFilter /> : null}
          <Filters />
          <Ordering />
          <Views />
          {flagListDisplayPreferences() ? <DisplayPreferences /> : null}
          <Action />
          <ResourceSettings />
          <Suspense fallback={null}>
            <QuietErrorBoundary>
              <ShortcutsTrigger />
            </QuietErrorBoundary>
          </Suspense>
          {hasInlineChangeHistory ? null : <ChangeHistoryTrigger />}
        </LayoutHeaderActions>
      </LayoutHeader>
      {banner}
      <div className={styles.scroller} data-scroller>
        {context}
        <div className={clsx(styles.main, { [styles.maxWidthAndCentered]: isMaxWidthAndCenteredLayoutContent })}>
          {content}
        </div>
      </div>
      {footer}
    </div>
  );
}

export function ProjectCollectionContext({ children, wide = false }: PropsWithChildren<{ wide?: boolean }>) {
  const isMaxWidthAndCentered = useMaxWidthAndCenteredLayoutContent();
  const [{ freeform, controls, environmentSelector }] = useSlots(children, {
    freeform: ProjectCollectionFreeform,
    controls: ProjectCollectionControls,
    environmentSelector: EnvironmentController,
  });
  const contextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const context = contextRef.current;
    if (!context || !environmentSelector) {
      return;
    }

    const scroller = context.closest('[data-scroller]');
    if (!scroller) {
      return;
    }

    const handleScroll = () => {
      const { scrollLeft, clientWidth, scrollWidth } = scroller;
      context.toggleAttribute('data-scrolled', scrollLeft > 0);
      context.toggleAttribute('data-scrolled-right', scrollLeft + clientWidth < scrollWidth - 1);
    };

    scroller.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => scroller.removeEventListener('scroll', handleScroll);
  }, [environmentSelector]);

  if (freeform) {
    return (
      <div className={clsx(styles.context, { [styles.maxWidthAndCentered]: isMaxWidthAndCentered })}>{freeform}</div>
    );
  }

  return (
    <div
      ref={contextRef}
      className={clsx(styles.context, {
        [styles.contextWide]: wide,
        [styles.maxWidthAndCentered]: isMaxWidthAndCentered,
      })}
      data-context
    >
      {controls}
      {environmentSelector && <div>{environmentSelector}</div>}
    </div>
  );
}

export function ProjectCollectionFreeform({ children }: PropsWithChildren) {
  return <div className={styles.freeform}>{children}</div>;
}

export function ProjectCollectionContent({ children }: PropsWithChildren) {
  return <div className={styles.content}>{children}</div>;
}

export function ProjectCollectionBanner({ children }: PropsWithChildren) {
  return <div className={styles.banner}>{children}</div>;
}

export function ProjectCollectionFooter({ children }: PropsWithChildren) {
  return <div className={styles.footer}>{children}</div>;
}

export function ProjectCollectionControls({ children }: PropsWithChildren) {
  return <div className={styles.controls}>{children}</div>;
}
